import Button from '@mui/material/Button'
import DialogContentText from '@mui/material/DialogContentText'
import Stack from '@mui/material/Stack'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import { getRouteName } from 'global/helpers/activeRoute'
import { analyticsEvent } from 'global/helpers/analytics'
import { handleFormSubmission, processServerErrorMessages } from 'global/helpers/form'
import { type ErrorMessage, handleErrorResponse } from 'global/helpers/requestError'
import { type ReactElement, useEffect, useState } from 'react'
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import ShipmentNumbersField from 'screens/StatusManager/ShipmentNumbersField'
import {
  getUpdatedStopsForAttachFiles,
  updateTripLegsCacheWithUpdatedStops,
} from 'screens/StatusManager/StatusManager.helpers'
import { TRIPS_QUERY_KEY } from 'screens/TripDashboard/TripDashboard.service'
import type { TripData } from 'screens/TripDashboard/TripDashboard.types'
import { TRIP_DETAILS_QUERY_KEY, tripLegsQueryOptions } from 'screens/TripDetails/TripDetails.service'
import type { StopLegPair } from 'screens/TripDetails/TripDetails.types'
import DocumentsUploadFields from './DocumentsUploadFields'
import { bulkAttachFiles } from './StatusManager.service'
import { AttachmentResultType, type BulkDocumentsUploadFormValues } from './StatusManager.types'

interface BulkDocumentsUploadDialogPayload {
  trip: TripData
  stopLegPairs: StopLegPair[]
  weblinkToken?: string
}

interface BulkDocumentsUploadDialogProps {
  payload: BulkDocumentsUploadDialogPayload
  open: boolean
  onAction: () => void
  onError: (errors: ErrorMessage[]) => void
  onClose: () => void
}

const BulkDocumentsUploadDialog = ({
  payload,
  open,
  onAction,
  onError,
  onClose,
}: BulkDocumentsUploadDialogProps): ReactElement => {
  const { t } = useTranslation()
  const location = useLocation()
  const [pendingFiles, setPendingFiles] = useState(false)
  const form = useForm<BulkDocumentsUploadFormValues>({
    criteriaMode: 'all',
  })
  const { handleSubmit, reset, formState, setError } = form
  const queryClient = useQueryClient()
  const bulkAttachFilesMutation = useMutation({
    mutationFn: (data: BulkDocumentsUploadFormValues) =>
      bulkAttachFiles(payload.trip.entityId, payload.stopLegPairs, data, payload.weblinkToken),
    onSuccess: async (_response, data, _result, context) => {
      const updatedStops = getUpdatedStopsForAttachFiles(payload.stopLegPairs, data)
      const queryKey = tripLegsQueryOptions(payload.trip.entityId, payload.weblinkToken).queryKey
      await updateTripLegsCacheWithUpdatedStops(updatedStops, queryKey, context.client)
    },
    onSettled: () => {
      setTimeout(async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [TRIP_DETAILS_QUERY_KEY, payload.trip.entityId] }),
          queryClient.invalidateQueries({ queryKey: [TRIPS_QUERY_KEY] }),
        ])
      }, 2000)
    },
  })

  useEffect(() => {
    if (open) {
      reset({
        shipmentNumbers: [],
      })
    }
  }, [open])

  const onSubmit: SubmitHandler<BulkDocumentsUploadFormValues> = async (data: BulkDocumentsUploadFormValues) => {
    await handleFormSubmission(async () => {
      const response = await bulkAttachFilesMutation.mutateAsync(data)
      const errors = Object.values(response).flat().flatMap((attachmentResult) =>
        attachmentResult.type === AttachmentResultType.FAILURE ? handleErrorResponse(attachmentResult.reasons) : []
      )
      if (errors.length > 0) {
        onError(errors)
      } else {
        onAction()
      }
      const hasSuccessfullyUpdatedStop = payload.stopLegPairs.some((stopLegPair) =>
        response[stopLegPair.stop.wayPointCid]?.some(
          (attachmentResult) => attachmentResult.type === AttachmentResultType.SUCCESS,
        ),
      )
      if (hasSuccessfullyUpdatedStop) {
        analyticsEvent('polestar_cs_documents_uploaded', [getRouteName(location.pathname)])
      }
    }, {
      onError: (error) => setError('root', processServerErrorMessages(error)),
    })
  }

  return (
    <FormProvider {...form}>
      <KNDialog
        open={open}
        onClose={onClose}
        closeLabel={t('general.cancel')}
        preventClosing={formState.isSubmitting}
        title={t('screens.cs.trip_details.card.actions.upload_documents')}
        actions={
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            loading={formState.isSubmitting || pendingFiles} // NOSONAR
            onClick={handleSubmit(onSubmit)}
          >
            {t('screens.cs.trip_details.card.actions.upload_documents')}
          </Button>
        }
        onSubmit={handleSubmit(onSubmit)}
      >
        <KNDialogFormErrors errors={formState.errors?.root} />
        <DialogContentText component="div" mb={2}>
          {t('screens.cs.trip_details.update_status.bulk_document_upload_disclaimer')}
        </DialogContentText>
        <Stack spacing={2}>
          <ShipmentNumbersField stopLegPairs={payload.stopLegPairs} availableStopLegPairs={payload.stopLegPairs} />
          <DocumentsUploadFields
            stopType={payload.stopLegPairs[0]!.stop.type}
            showProof
            showPhotos={payload.trip.sendingApplication !== 'MACADAM'}
            onPending={setPendingFiles}
            weblinkToken={payload.weblinkToken}
          />
        </Stack>
      </KNDialog>
    </FormProvider>
  )
}

export default BulkDocumentsUploadDialog
